/** Outage map (spec §8): Leaflet, one marker per active outage from the
 *  GET /oms/map/outages AGGREGATE feature collection, colored by scope
 *  (Red feeder/substation, Orange transformer, Yellow meter). ~30s auto-refresh
 *  (baked into `useOutageMap`). Clicking a marker selects the outage.
 *
 *  The aggregate endpoint returns `geometry: null` on the wire (to avoid a
 *  per-meter geometry fan-out — see services/oms/api_service.map_features), so
 *  this component RESOLVES each feature's location client-side by joining its
 *  scope + device_id against the existing topology map layers
 *  (substations/feeders/transformers/meters). Substations/feeders/transformers
 *  are bulk layers, but the meter layer is bbox-gated by design (perf — no
 *  unbounded meter fan-out; see useMetersLayer), so meter geometry is fetched
 *  for the CURRENT VIEWPORT via a BoundsTracker and refreshed on pan/zoom. A
 *  meter-scope outage therefore only resolves once its meter falls inside the
 *  view; those still unresolved are reported separately ("zoom/pan to place")
 *  from device-scope outages that are genuinely missing geometry, rather than
 *  being silently lumped into one "unplaced" count.
 *
 *  Source markers come from the server's `dispatch_*` properties (spec
 *  2026-09-01 §C): a pulsing diamond at the point a crew should be sent,
 *  colored by which device it is (confirmed / suggested / the outage's own),
 *  joined to its outage circle by a dashed connector when the two are far
 *  enough apart to read as separate places. */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Polyline,
  Popup,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import {
  useFeedersLayer,
  useSubstationsLayer,
  useTransformersLayer,
  useMetersLayer,
} from '../../hooks/useMapLayers.ts'
import { useOutageMap } from '../../hooks/useOms.ts'
import type { BBox } from '../../api/map.ts'
import type { DispatchTarget, OutageScope } from '../../types/oms.ts'
import { OUTAGE_SCOPE_COLORS } from '../../types/oms.ts'
import { deviceLabel } from './omsFormat.tsx'
import { collectSourceMarkers } from './outageMapMarkers.ts'
import { addressLine, targetLabel } from './dispatchFormat.ts'
import type {
  GeoJSONFeatureCollection,
  GeoJSONLineString,
  GeoJSONPoint,
  GeoJSONPolygon,
} from '../../types/topology.ts'

// Cache Valley, UT center (shared with the main MapView).
const DEFAULT_CENTER: [number, number] = [41.7355, -111.8338]
const DEFAULT_ZOOM = 12

export interface LocateRequest {
  id: number
  nonce: number
}

const SOURCE_COLORS: Record<DispatchTarget, string> = {
  suggested_device: '#7c3aed',
  confirmed_device: '#059669',
  event_device: '#6b7280',
}

// Below this separation the source diamond and the outage circle overlap
// visually, so a dashed connector would just clutter the map.
const CONNECTOR_MIN_M = 20

/** divIcon per (target, selected): the diamond + a halo that pulses for
 *  suggested/confirmed devices; the outage's own device is static grey. */
const iconCache = new Map<string, L.DivIcon>()
function sourceIcon(target: DispatchTarget, selected: boolean): L.DivIcon {
  const key = `${target}:${selected}`
  let icon = iconCache.get(key)
  if (!icon) {
    const size = selected ? 24 : 18
    const halo = target === 'event_device' ? '' : '<div class="oms-source-halo"></div>'
    icon = L.divIcon({
      className: '',
      html:
        `<div class="oms-source${selected ? ' oms-source--selected' : ''}" ` +
        `style="color:${SOURCE_COLORS[target]}">${halo}<div class="oms-source-diamond"></div></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    })
    iconCache.set(key, icon)
  }
  return icon
}

/** Leaflet sizes itself once on mount; when the container changes size
 *  (full-screen toggle) it must be told, next frame, after layout. */
function ResizeNudge({ expanded }: { expanded: boolean }) {
  const map = useMap()
  useEffect(() => {
    const id = requestAnimationFrame(() => map.invalidateSize())
    return () => cancelAnimationFrame(id)
  }, [map, expanded])
  return null
}

/** Fly to a requested outage: its source marker, else its own circle. Keyed on
 *  the nonce — the request object's identity changes with every click, so
 *  repeated clicks on the same id fly again.
 *
 *  `targets` is a fresh `Map` every time the placement memo recomputes, which
 *  happens on every `moveend` (the bbox-gated meter layer refetches). Without
 *  a guard that would re-run this effect — and re-fly the map — on every pan,
 *  snapping the view straight back to the last-located outage. `handledRef`
 *  remembers the nonce we've already flown to, so a `targets` recompute alone
 *  is a no-op; only a genuinely new request (a new nonce) flies again. If the
 *  request's target isn't in `targets` yet (its meter hasn't loaded), the ref
 *  is left unset so a later recompute can still satisfy this same request. */
function FlyTo({
  request, targets,
}: {
  request: LocateRequest | null | undefined
  targets: Map<number, [number, number]>
}) {
  const map = useMap()
  const handledRef = useRef<number | null>(null)
  useEffect(() => {
    if (!request || request.nonce === handledRef.current) return
    const pos = targets.get(request.id)
    if (!pos) return
    map.flyTo(pos, Math.max(map.getZoom(), 16), { duration: 0.6 })
    handledRef.current = request.nonce
  }, [map, request, targets])
  return null
}

type AnyGeom = GeoJSONPoint | GeoJSONLineString | GeoJSONPolygon | null

/** Representative [lat, lng] for a geometry (Point → itself, LineString /
 *  Polygon → midpoint / first ring centroid). GeoJSON is [lng, lat]. */
function representativePoint(geom: AnyGeom): [number, number] | null {
  if (!geom) return null
  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates
    return [lat, lng]
  }
  if (geom.type === 'LineString') {
    const coords = geom.coordinates
    if (coords.length === 0) return null
    const [lng, lat] = coords[Math.floor(coords.length / 2)]
    return [lat, lng]
  }
  if (geom.type === 'Polygon') {
    const ring = geom.coordinates[0]
    if (!ring || ring.length === 0) return null
    let sx = 0
    let sy = 0
    for (const [lng, lat] of ring) {
      sx += lng
      sy += lat
    }
    return [sy / ring.length, sx / ring.length]
  }
  return null
}

/** Build a device_id → representative point index from a layer collection. */
function indexLayer(
  fc: GeoJSONFeatureCollection | undefined,
): Map<number, [number, number]> {
  const idx = new Map<number, [number, number]>()
  if (!fc?.features) return idx
  for (const f of fc.features) {
    const id = f.properties?.id
    if (typeof id !== 'number') continue
    const pt = representativePoint(f.geometry as AnyGeom)
    if (pt) idx.set(id, pt)
  }
  return idx
}

/** Reports the current viewport bounds so the bbox-gated meter layer can fetch
 *  only the meters in view (mirrors MapView's BoundsTracker idiom). */
function BoundsTracker({ onBoundsChange }: { onBoundsChange: (bbox: BBox) => void }) {
  const map = useMapEvents({
    moveend() {
      const b = map.getBounds()
      onBoundsChange({
        minLon: b.getWest(),
        minLat: b.getSouth(),
        maxLon: b.getEast(),
        maxLat: b.getNorth(),
      })
    },
  })
  // Emit once on mount (moveend doesn't fire for the initial view).
  useEffect(() => {
    const b = map.getBounds()
    onBoundsChange({
      minLon: b.getWest(),
      minLat: b.getSouth(),
      maxLon: b.getEast(),
      maxLat: b.getNorth(),
    })
  }, [map, onBoundsChange])
  return null
}

interface PlacedOutage {
  id: number
  scope: OutageScope
  /** NULL for an unresolved meter-scope event (no GIS crosswalk). */
  deviceId: number | null
  unresolvedMeterNumber: string | null
  status: string
  customersAffected: number | null
  criticalAffected: number | null
  criticalProtected: boolean
  etr: string | null
  position: [number, number]
  /** Child of a LIVE parent — drawn with a thinner stroke than a root event. */
  hasLiveParent: boolean
  /** Server suggestion payload (device type/label/phases) — titles the popup
   *  for a device-scope event. */
  suggestionDetail: Record<string, unknown> | null
}

export function OutageMap({
  onSelect,
  selectedId,
  expanded = false,
  onToggleExpanded,
  locateRequest,
  controlsInsetRight = 0,
}: {
  onSelect: (outageId: number) => void
  selectedId: number | null
  expanded?: boolean
  onToggleExpanded?: () => void
  locateRequest?: LocateRequest | null
  /** Pixels to shift the full-screen toggle and legend left, so the docked
   *  detail drawer (full-screen overlay only) doesn't cover them. */
  controlsInsetRight?: number
}) {
  const { data: outageFc, isLoading, isError } = useOutageMap({ status: 'active' })
  // Current viewport bbox — drives the (bbox-gated) meter layer so meter-scope
  // outages in view get real geometry instead of falling into "unplaced".
  const [bbox, setBbox] = useState<BBox | undefined>(undefined)
  const handleBoundsChange = useCallback((next: BBox) => setBbox(next), [])
  // Topology layers for client-side geometry resolution.
  const { data: substations } = useSubstationsLayer()
  const { data: feeders } = useFeedersLayer()
  const { data: transformers } = useTransformersLayer()
  const { data: meters } = useMetersLayer({ bbox })

  const indexes = useMemo(
    () => ({
      substation: indexLayer(substations as GeoJSONFeatureCollection | undefined),
      feeder: indexLayer(feeders as GeoJSONFeatureCollection | undefined),
      transformer: indexLayer(transformers as GeoJSONFeatureCollection | undefined),
      meter: indexLayer(meters as GeoJSONFeatureCollection | undefined),
    }),
    [substations, feeders, transformers, meters],
  )

  const { placed, sources, unplaced, meterPending } = useMemo(() => {
    const out: PlacedOutage[] = []
    // Non-meter events (substation/feeder/device/transformer) we truly can't place
    // vs. meter-scope events whose meter simply isn't in the current viewport
    // (their layer is bbox-gated) — the latter resolve by panning/zooming.
    let missing = 0
    let pending = 0
    const features = (outageFc as GeoJSONFeatureCollection | undefined)?.features ?? []
    const sources = collectSourceMarkers(features)
    for (const f of features) {
      const p = f.properties ?? {}
      const scope = p.scope as OutageScope
      // device_id is NULL for an unresolved meter-scope event (no GIS
      // crosswalk) — it can never join a topology layer, so it only places if
      // the server ever ships geometry.
      const deviceId = (p.device_id as number | null | undefined) ?? null
      // Prefer feature geometry if the server ever provides it; else resolve by
      // scope+device against the topology layers.
      const geomPt = representativePoint(f.geometry as AnyGeom)
      // A device-scope event has NO topology layer to join (protective devices
      // aren't drawn as a layer), so it places at the dispatch point the
      // server already computed for it.
      const dispatchPt: [number, number] | null =
        scope === 'device' &&
        typeof p.dispatch_lat === 'number' &&
        typeof p.dispatch_lon === 'number'
          ? [p.dispatch_lat, p.dispatch_lon]
          : null
      const layerPt =
        scope !== 'device' && deviceId !== null
          ? (indexes[scope]?.get(deviceId) ?? null)
          : null
      const position = geomPt ?? dispatchPt ?? layerPt ?? null
      if (!position) {
        if (scope === 'meter' && deviceId !== null) pending += 1
        else missing += 1
        continue
      }
      out.push({
        id: p.id as number,
        scope,
        deviceId,
        unresolvedMeterNumber: (p.unresolved_meter_number as string | null | undefined) ?? null,
        status: p.status as string,
        customersAffected: (p.customers_affected as number | null) ?? null,
        criticalAffected: (p.critical_customers_affected as number | null) ?? null,
        criticalProtected: Boolean(p.critical_customers_protected),
        etr: (p.estimated_restoration_at as string | null | undefined) ?? null,
        position,
        hasLiveParent: p.has_live_parent === true,
        suggestionDetail:
          (p.suggestion_detail as Record<string, unknown> | null | undefined) ?? null,
      })
    }
    return { placed: out, sources, unplaced: missing, meterPending: pending }
  }, [outageFc, indexes])

  // Locate targets: the source point when it exists, else the outage circle.
  const locateTargets = useMemo(() => {
    const m = new Map<number, [number, number]>()
    for (const o of placed) m.set(o.id, o.position)
    for (const s of sources) m.set(s.outageId, s.position)
    return m
  }, [placed, sources])
  const placedById = useMemo(() => new Map(placed.map(o => [o.id, o])), [placed])

  return (
    <div className="relative h-full w-full">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={10}
        maxZoom={19}
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // Leaflet's TileLayer defaults to maxZoom 18; the MapContainer allows 19
          // and "Locate" flies to >= 16, so without this the basemap goes blank
          // at the last zoom step (found on the 2026-09-01 smoke walk).
          maxZoom={19}
        />
        <BoundsTracker onBoundsChange={handleBoundsChange} />
        <ResizeNudge expanded={expanded} />
        <FlyTo request={locateRequest} targets={locateTargets} />
        {placed.map(o => {
          const isFeederScope = o.scope === 'feeder' || o.scope === 'substation'
          return (
            <CircleMarker
              key={o.id}
              center={o.position}
              radius={
                o.id === selectedId
                  ? 12
                  : isFeederScope
                    ? 10
                    : o.scope === 'device'
                      ? 9
                      : o.scope === 'transformer'
                        ? 7
                        : 5
              }
              pathOptions={{
                color: '#333',
                weight: o.id === selectedId ? 3 : o.hasLiveParent ? 1 : 2,
                fillColor: OUTAGE_SCOPE_COLORS[o.scope],
                fillOpacity: 0.85,
              }}
              eventHandlers={{ click: () => onSelect(o.id) }}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold">Outage #{o.id}</div>
                  <div>
                    {deviceLabel(o.scope, o.deviceId, o.unresolvedMeterNumber, o.suggestionDetail)}{' '}
                    · {o.status}
                  </div>
                  <div>Customers: {o.customersAffected ?? '--'}</div>
                  <div>
                    Critical:{' '}
                    {o.criticalProtected
                      ? 'protected'
                      : (o.criticalAffected ?? 0)}
                  </div>
                  {o.etr && (
                    <div>ETR: {new Date(o.etr).toLocaleString()}</div>
                  )}
                  <button
                    onClick={() => onSelect(o.id)}
                    className="mt-1 text-blue-600 hover:underline"
                  >
                    Open detail →
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          )
        })}
        {sources.map(s => {
          const own = placedById.get(s.outageId)
          const far =
            own && L.latLng(own.position).distanceTo(L.latLng(s.position)) > CONNECTOR_MIN_M
          return (
            <Fragment key={`src-${s.outageId}`}>
              {far && (
                <Polyline
                  positions={[s.position, own.position]}
                  pathOptions={{ color: SOURCE_COLORS[s.target], weight: 2, dashArray: '4 4', opacity: 0.8 }}
                  interactive={false}
                />
              )}
              <Marker
                position={s.position}
                icon={sourceIcon(s.target, s.outageId === selectedId)}
                zIndexOffset={s.outageId === selectedId ? 2000 : 1000}
                eventHandlers={{ click: () => onSelect(s.outageId) }}
              >
                <Popup>
                  <div className="text-xs space-y-0.5">
                    <div className="font-semibold">
                      {targetLabel(s.target)} · outage #{s.outageId}
                    </div>
                    {s.location?.device_label && (
                      <div>
                        {s.location.device_label}
                        {s.location.device_type ? ` (${s.location.device_type})` : ''}
                      </div>
                    )}
                    {s.location?.pole_id && <div>Pole {s.location.pole_id}</div>}
                    <div>{addressLine(s.location)}</div>
                    <button
                      onClick={() => onSelect(s.outageId)}
                      className="mt-1 text-blue-600 hover:underline"
                    >
                      Open detail →
                    </button>
                  </div>
                </Popup>
              </Marker>
            </Fragment>
          )
        })}
      </MapContainer>

      {onToggleExpanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-pressed={expanded}
          title={expanded ? 'Exit full screen (Esc)' : 'Full screen'}
          className="absolute top-3 z-[1000] w-8 h-8 rounded-md bg-white/95 border border-gray-200 shadow-md text-gray-700 hover:bg-gray-50 text-base leading-none"
          style={{ right: 12 + controlsInsetRight }}
        >
          {expanded ? '⤡' : '⤢'}
        </button>
      )}

      {/* Legend */}
      <div
        className="absolute bottom-3 z-[1000] bg-white/95 rounded-lg shadow-md border border-gray-200 px-3 py-2 text-xs"
        style={{ right: 12 + controlsInsetRight }}
      >
        <div className="font-medium text-gray-700 mb-1">Outage scope</div>
        {(['feeder', 'device', 'transformer', 'meter'] as OutageScope[]).map(s => (
          <div key={s} className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: OUTAGE_SCOPE_COLORS[s] }}
            />
            <span className="text-gray-600 capitalize">
              {s === 'feeder' ? 'Feeder / substation' : s === 'device' ? 'Protective device' : s}
            </span>
          </div>
        ))}
        {(
          [
            ['suggested_device', 'Source · suggested'],
            ['confirmed_device', 'Source · confirmed'],
          ] as [DispatchTarget, string][]
        ).map(([t, label], i) => (
          <div key={t} className={`flex items-center gap-1.5 ${i === 0 ? 'mt-1 pt-1 border-t border-gray-100' : ''}`}>
            <span
              className="w-2.5 h-2.5 shrink-0"
              style={{ backgroundColor: SOURCE_COLORS[t], transform: 'rotate(45deg)' }}
            />
            <span className="text-gray-600">{label}</span>
          </div>
        ))}
        {meterPending > 0 && (
          <div className="mt-1 pt-1 border-t border-gray-100 text-amber-600">
            {meterPending} meter outage(s) outside view — pan/zoom to place
          </div>
        )}
        {unplaced > 0 && (
          <div className="mt-1 pt-1 border-t border-gray-100 text-amber-600">
            {unplaced} outage(s) without map location
          </div>
        )}
      </div>

      {/* Status overlay */}
      {(isLoading || isError) && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1.5 rounded-md shadow-md text-xs bg-white border border-gray-200">
          {isLoading ? 'Loading outages…' : 'Failed to load outage map.'}
        </div>
      )}
    </div>
  )
}
